/**
 * Grade de atendimento (expediente) — fonte única do lado do cliente.
 *
 * A configuração `clinics/{cid}/config/agenda.days[weekday]` sempre teve
 * `enabled/start/end` (+ `breaks`), mas a AGENDA lia só os `breaks`: a
 * grade do dia era desenhada 07:00–20:00 fixo, sem nenhuma marca de onde
 * o expediente começa e termina. Resultado: horário liberado e horário
 * fora do expediente tinham exatamente a mesma aparência, e um paciente
 * marcado às 07:30 num dia que abre 09:00 não se distinguia dos demais.
 *
 * Este módulo responde às duas perguntas que faltavam:
 *   - `resolveDayGrade` — qual é o expediente DESTE dia?
 *   - `getEncaixeReason` — este horário está fora dele? por quê?
 *
 * Desde a spec 010 a agenda também sabe responder "qual é o expediente
 * DESTE médico neste dia" (`resolveProfessionalDay`), com a MESMA
 * hierarquia que o portal do paciente já usa no backend
 * (`getEffectiveDayConfig` / `resolveProfForDate` em
 * `functions/src/proxy/portal.ts`). As duas cópias precisam concordar:
 * se divergirem, o portal e a secretária passam a discordar sobre quem
 * atende quando — o pior bug possível aqui.
 *
 * Nota deliberada: o motivo é DERIVADO na hora de desenhar, não gravado
 * no agendamento. Isso significa que mudar o expediente re-rotula o
 * passado — aceitável porque o selo é informativo (não trava nada, não
 * entra em cobrança). Se algum dia ele virar regra dura, o motivo tem
 * que virar snapshot na criação.
 */

import type { AgendaBreak, AgendaConfigDay } from '@/types/models';
import { timeToMinutes } from './blockedDates';

const HHMM = /^\d{2}:\d{2}$/;

/** Expediente de um dia concreto, já em minutos e com os intervalos limpos. */
export interface DayGrade {
  /** A clínica atende neste dia da semana? */
  enabled: boolean;
  /** Início do expediente (min desde a meia-noite). */
  startMin: number;
  /** Fim do expediente, EXCLUSIVO (min desde a meia-noite). */
  endMin: number;
  /** Intervalos sem atendimento dentro do expediente (almoço etc.). */
  breaks: { startMin: number; endMin: number; label: string }[];
}

/** Por que um horário está fora da grade. `null` = dentro do expediente. */
export type EncaixeReason = 'DIA_FECHADO' | 'FORA_DE_EXPEDIENTE' | 'INTERVALO';

export const ENCAIXE_REASON_LABEL: Record<EncaixeReason, string> = {
  DIA_FECHADO: 'Dia sem expediente',
  FORA_DE_EXPEDIENTE: 'Fora do horário de atendimento',
  INTERVALO: 'Durante o intervalo',
};

/**
 * `config.days` cru do Firestore → mapa por dia da semana.
 *
 * Defensivo de propósito: docs antigos não têm `days` (o doc nasceu
 * guardando só `blockedDates`), têm dias parciais, ou têm `start`/`end`
 * em formato inesperado. Dia que não parseia simplesmente não entra no
 * mapa — e sem grade a agenda não pinta nada, que é o lado seguro (não
 * inventa expediente que a clínica não configurou).
 */
export function parseAgendaDays(raw: unknown): Record<number, AgendaConfigDay> {
  const out: Record<number, AgendaConfigDay> = {};
  if (!raw || typeof raw !== 'object') return out;

  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const weekday = Number(k);
    if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) continue;
    if (!v || typeof v !== 'object') continue;

    const d = v as { enabled?: unknown; start?: unknown; end?: unknown; breaks?: unknown };
    const start = typeof d.start === 'string' && HHMM.test(d.start) ? d.start : null;
    const end = typeof d.end === 'string' && HHMM.test(d.end) ? d.end : null;
    if (!start || !end || timeToMinutes(end) <= timeToMinutes(start)) continue;

    const breaks: AgendaBreak[] = Array.isArray(d.breaks)
      ? (d.breaks as unknown[])
          .map((b) => {
            const e = b as { start?: unknown; end?: unknown; label?: unknown };
            return {
              start: typeof e?.start === 'string' ? e.start : '',
              end: typeof e?.end === 'string' ? e.end : '',
              label: typeof e?.label === 'string' ? e.label : undefined,
            };
          })
          .filter((b) => HHMM.test(b.start) && HHMM.test(b.end) && timeToMinutes(b.end) > timeToMinutes(b.start))
      : [];

    out[weekday] = {
      // `enabled` ausente conta como ligado: o dia tem horário configurado.
      enabled: d.enabled !== false,
      start,
      end,
      breaks,
    };
  }
  return out;
}

/**
 * Grade do dia em foco. `null` = sem configuração pra esse dia da semana
 * (a agenda então não pinta nada, em vez de chutar um expediente).
 */
export function resolveDayGrade(
  daysByWeekday: Record<number, AgendaConfigDay>,
  weekday: number,
  /** Reservado: ver nota do topo. Hoje só existe a grade da clínica. */
  _professionalId?: string,
): DayGrade | null {
  const day = daysByWeekday[weekday];
  if (!day) return null;
  return {
    enabled: day.enabled,
    startMin: timeToMinutes(day.start),
    endMin: timeToMinutes(day.end),
    breaks: (day.breaks ?? []).map((b) => ({
      startMin: timeToMinutes(b.start),
      endMin: timeToMinutes(b.end),
      label: b.label?.trim() || 'Intervalo',
    })),
  };
}

/**
 * Este horário é encaixe? Compara o INÍCIO do atendimento com a grade.
 *
 * Usa o início (e não a sobreposição da duração inteira) porque é o
 * horário que a recepção escolheu — uma consulta das 16:50 num dia que
 * fecha 17:00 é dentro da grade, mesmo transbordando alguns minutos.
 * Encavalar o fim do expediente é rotina; começar fora dele é a exceção
 * que vale sinalizar.
 */
export function getEncaixeReason(startTime: string, grade: DayGrade | null): EncaixeReason | null {
  if (!grade) return null;
  if (!grade.enabled) return 'DIA_FECHADO';
  if (!HHMM.test(startTime)) return null;

  const min = timeToMinutes(startTime);
  if (!Number.isFinite(min)) return null;
  if (min < grade.startMin || min >= grade.endMin) return 'FORA_DE_EXPEDIENTE';
  if (grade.breaks.some((b) => min >= b.startMin && min < b.endMin)) return 'INTERVALO';
  return null;
}

/* ───────────────────────── Expediente por médico (spec 010) ───────────────────────── */

/** Exceção pontual por data (mesma forma de `Professional.unitDateOverrides`). */
export interface ProfDateOverride {
  date: string;   // 'YYYY-MM-DD'
  unitId: string;
  start?: string; // 'HH:MM'
  end?: string;
}

/** Só a parte de agenda do `Professional` — o resto do cadastro não importa aqui. */
export interface ProfessionalSchedule {
  unitSchedules?: Record<string, { days?: Record<string, unknown> }>;
  unitDateOverrides?: ProfDateOverride[];
}

/** Dia fechado — o médico não atende. Usado quando ele está em outra unidade. */
const CLOSED_DAY: AgendaConfigDay = { enabled: false, start: '00:00', end: '00:00', breaks: [] };

/**
 * Expediente efetivo de UM médico numa data concreta.
 *
 * Hierarquia (mais específico ganha), igual à do portal:
 *   1. `unitDateOverrides` da data — "dia 10/04 atendo na Filial", "sábado
 *      de plantão". Override SEMPRE habilita o dia; horário omitido herda
 *      o da semana. Se o override aponta pra OUTRA unidade, o médico não
 *      está aqui neste dia → dia fechado.
 *   2. `unitSchedules[unitId].days[dow]` — a semana dele nesta unidade.
 *   3. `clinicDays[dow]` — a config da clínica (comportamento de sempre).
 *
 * Médico sem nada configurado cai direto no 3: quem não preencheu não muda
 * de comportamento.
 *
 * `null` = nem o médico nem a clínica definiram este dia — a agenda então
 * não pinta nada, em vez de inventar expediente.
 */
export function resolveProfessionalDay(
  prof: ProfessionalSchedule | null | undefined,
  unitId: string,
  dateStr: string,
  weekday: number,
  clinicDays: Record<number, AgendaConfigDay>,
): AgendaConfigDay | null {
  const clinicDay = clinicDays[weekday] ?? null;
  if (!prof) return clinicDay;

  /** Um dia do `unitSchedules` cru → dia válido, ou null. Reaproveita o
   *  parser defensivo: a origem é o mesmo tipo de dado meia-boca. */
  const weekDayOf = (uid: string): AgendaConfigDay | null => {
    const raw = prof.unitSchedules?.[uid]?.days;
    if (!raw || typeof raw !== 'object') return null;
    return parseAgendaDays(raw)[weekday] ?? null;
  };

  const overrides = Array.isArray(prof.unitDateOverrides) ? prof.unitDateOverrides : [];
  const match = overrides.find((o) => o && o.date === dateStr && typeof o.unitId === 'string');
  if (match) {
    // Deslocamento: neste dia ele atende SÓ na unidade do override.
    if (match.unitId !== unitId) return CLOSED_DAY;
    const base = weekDayOf(match.unitId) ?? clinicDay;
    const start = HHMM.test(match.start ?? '') ? match.start! : base?.start;
    const end = HHMM.test(match.end ?? '') ? match.end! : base?.end;
    if (!start || !end || timeToMinutes(end) <= timeToMinutes(start)) return base ?? null;
    return {
      enabled: true, // existir override significa "vou atender"
      start,
      end,
      // O override muda a janela do dia, não o almoço dele.
      breaks: base?.breaks ?? [],
    };
  }

  return weekDayOf(unitId) ?? clinicDay;
}

/**
 * Grade do dia de um médico — mesmo retorno de `resolveDayGrade`, mas
 * resolvendo o expediente dele antes. Sem médico escolhido, o callsite
 * continua usando `resolveDayGrade` (grade da clínica).
 */
export function resolveProfessionalDayGrade(
  prof: ProfessionalSchedule | null | undefined,
  unitId: string,
  dateStr: string,
  weekday: number,
  clinicDays: Record<number, AgendaConfigDay>,
): DayGrade | null {
  const day = resolveProfessionalDay(prof, unitId, dateStr, weekday, clinicDays);
  if (!day) return null;
  return resolveDayGrade({ [weekday]: day }, weekday);
}
